/*
 * How a star's magnitude becomes pixels, as a set of interchangeable curves.
 *
 * There is no single correct answer here, which is why this is a dial rather
 * than a constant. The physics fixes the *ratios* — five magnitudes is exactly a
 * factor of 100 in flux — but a screen has perhaps 8 stops of usable range
 * against a sky that spans about 12, and the eye's own response to a faint point
 * source at night is not the one it uses in daylight. Something has to be
 * compressed, and the choice of how much is a judgement about what a sky *looks*
 * like, not a calculation.
 *
 * Four knobs, in rough order of how much they matter:
 *
 *   gamma      Compression. The flux ratio is raised to 1/gamma. Higher lifts
 *              the faint end toward the bright end, which is what makes a sky
 *              read as crowded; lower pulls them apart and lets the bright stars
 *              dominate. This is the main dial.
 *   alphaFloor The opacity a just-visible star still gets. This is the one most
 *              likely to be the actual culprit in a sky that feels too busy: a
 *              floor means the faintest star in the catalogue can never fade
 *              below it however the gamma is set.
 *   magLimit   Where the sky runs out. Real limits are soft, so this fades over
 *              the last two thirds of a magnitude rather than cutting. A true
 *              dark site reaches about 6.5, ordinary suburbs about 5, and the
 *              edge of a city closer to 4.5.
 *   coreFloor  The minimum radius of a sprite's flat centre, in pixels. It has
 *              to stay near a pixel or rasterising averages the peak away and
 *              every star comes out grey — but the floor also props up stars
 *              that ought to be vanishing, so the harsher presets lower it.
 */

const SIRIUS_FLUX = 10 ** (-0.4 * -1.46);

/*
 * Ordered from the most generous faint end to the harshest, which is the order
 * the settings slider runs in and a test pins.
 *
 * **Keys and labels diverge, deliberately.** A key is the value in a player's
 * localStorage and the name this curve goes by in the code, so renaming one
 * silently resets everybody who chose it; a label is what somebody reads in the
 * settings menu, where "legacy" and "crisp" mean nothing. The labels name the
 * sky each curve is drawing — Extra dark, Dark sky, Rural, Suburban — because
 * that is the thing the dial is really choosing between: how much light
 * pollution is in the sky you are standing under, which runs the same way as
 * how much of the faint end survives.
 *
 * `milkyWay` scales the band, and it falls away faster than the stars do
 * because that is the order in which a real sky loses them: the Milky Way is
 * the first casualty of light pollution and is long gone from a suburb that
 * still shows you a few hundred stars. Before this the band was drawn at one
 * fixed gain regardless of the setting, so turning the sky down toward Suburban
 * dimmed the stars and left the band exactly where it was — which made it more
 * dominant the darker you asked the sky to be, precisely backwards.
 */
export const BRIGHTNESS_PRESETS = [
  {
    key: 'legacy',
    milkyWay: 1.3,
    label: 'Extra dark',
    /*
     * Retuned to keep its peak and give back some of its faint end. The bright
     * stars are what "Bright" is for; the lifted floor underneath them was just
     * haze, and it was the thing that made this preset read as washed out rather
     * than as bright.
     *
     * The peak is untouched by construction. `gamma` cannot move it — the
     * brightest star in the sky is flux ratio 1, and 1 to any power is 1 — and
     * the alpha floor cannot either, because floor plus gain still clamps past
     * 1 at the top. Only the radius needed arithmetic: base came down 0.09 and
     * gain went up by the same, so base + gain is still 3.35.
     */
    note: 'A sky with no light pollution at all: the faintest stars still show.',
    gamma: 2.65,
    alphaFloor: 0.17,
    alphaGain: 1.15,
    radiusBase: 0.36,
    radiusGain: 2.99,
    coreFloor: 0.85,
    magLimit: 7.0
  },
  {
    key: 'crisp',
    milkyWay: 1.0,
    label: 'Dark sky',
    note: 'The default. Same stars, faint ones pulled back so the bright ones lead.',
    gamma: 2.45,
    alphaFloor: 0.13,
    alphaGain: 1.25,
    radiusBase: 0.4,
    radiusGain: 3.0,
    coreFloor: 0.9,
    magLimit: 7.0
  },
  {
    key: 'darksky',
    milkyWay: 0.5,
    label: 'Rural',
    note: 'Everything still there, but the bright stars clearly carry the sky.',
    gamma: 2.1,
    alphaFloor: 0.06,
    alphaGain: 1.3,
    radiusBase: 0.35,
    radiusGain: 3.2,
    coreFloor: 0.85,
    magLimit: 7.0
  },
  {
    key: 'suburban',
    milkyWay: 0.12,
    label: 'Suburban',
    note: 'The sky most people actually know: the sixth magnitude is simply gone.',
    gamma: 2.0,
    alphaFloor: 0.05,
    alphaGain: 1.35,
    radiusBase: 0.35,
    radiusGain: 3.3,
    coreFloor: 0.8,
    magLimit: 5.3
  }
];

export const DEFAULT_PRESET = 'crisp';

/*
 * Falls back to the default rather than to the first entry: the list is ordered
 * brightest-first for the tuner, so an unrecognised key landing on entry zero
 * would quietly serve the old look instead of the shipped one.
 */
export function presetByKey(key) {
  return (
    BRIGHTNESS_PRESETS.find((preset) => preset.key === key) ||
    BRIGHTNESS_PRESETS.find((preset) => preset.key === DEFAULT_PRESET)
  );
}

// Width of the soft edge at the limiting magnitude, in magnitudes.
const FADE_WIDTH = 0.7;

export function makeCurve(key) {
  const preset = presetByKey(key);

  return {
    preset,

    // Flux relative to the brightest star in the sky, before compression.
    flux: (mag) => 10 ** (-0.4 * mag) / SIRIUS_FLUX,

    brightness(mag) {
      return (10 ** (-0.4 * mag) / SIRIUS_FLUX) ** (1 / preset.gamma);
    },

    alpha(brightness) {
      return Math.min(1, preset.alphaFloor + preset.alphaGain * brightness);
    },

    radius(brightness) {
      return preset.radiusBase + preset.radiusGain * brightness;
    },

    coreFloor: preset.coreFloor,
    milkyWay: preset.milkyWay,

    /*
     * The soft edge of visibility. Returns 1 well inside the limit, 0 past it,
     * and a smoothstep between — a hard cut would put a suspiciously clean line
     * through the catalogue.
     */
    magFade(mag) {
      const start = preset.magLimit - FADE_WIDTH;
      if (mag <= start) return 1;
      if (mag >= preset.magLimit) return 0;
      const t = 1 - (mag - start) / FADE_WIDTH;
      return t * t * (3 - 2 * t);
    }
  };
}
